import { ReactNode, Suspense, memo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { NotIOsAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { CoinsAndBalances } from '@sorare/core/src/components/navigation/AppHeader/CoinsAndBalances';
import { LandingAppHeader } from '@sorare/core/src/components/navigation/AppHeader/LandingAppHeader';
import { MasterSwitch } from '@sorare/core/src/components/navigation/MasterSwitch';
import { ResponsiveSearchBar } from '@sorare/core/src/components/search/ResponsiveSearchBar';
import { ANY_SPORT_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { lazy } from '@sorare/core/src/lib/retry';
import { Balances } from '@sorare/core/src/routing/MultiSportAppBar/Balances';
import {
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

const Notifications = lazy(
  async () => import('components/notification/Notifications'),
  'Notifications'
);

const NavigationCart = lazy(
  async () => import('@sorare/marketplace/src/components/cart/NavigationCart'),
  'NavigationCart'
);

const Container = styled(FullWidthContainer)`
  border-bottom: 1px solid var(--c-nd-100);
`;

const Left = styled(Horizontal)`
  grid-area: left;
  flex-shrink: 1;
  min-width: 0;
`;

const SubNav = styled(Horizontal).attrs({ id: 'subnav', gap: 0.5 })`
  @media ${tabletAndAbove} {
    gap: var(--unit);
  }
`;

const WebWrapper = styled.div`
  height: 100%;
  gap: var(--unit);
  display: grid;
  grid-template-areas: 'left buttons';
  grid-template-columns: 1fr max-content;
  align-items: center;
  padding: var(--unit) 0;
  @media ${tabletAndAbove} {
    padding: var(--intermediate-unit) 0;
    align-items: center;
  }
`;

const IOSWrapper = styled.div`
  /**
  Page header should be handled by the ios app,
  also, display: none; doesn't prevent portal to work
  */

  display: none;
  width: 100%;
`;

const Buttons = styled.div`
  grid-area: buttons;
  display: flex;
  justify-content: flex-end;
  @media ${mobileAndAbove} {
    justify-content: space-between;
    align-items: center;
  }
  > * {
    justify-content: flex-end;
  }
`;

const PageHeader = styled(Horizontal).attrs({ gap: 0.5 })`
  flex: 1;
  max-width: 100%;
  @media ${tabletAndAbove} {
    gap: var(--double-unit);
  }
`;
const PageHeaderRightButton = styled(Horizontal).attrs({ gap: 0.5 })`
  margin-left: auto;
`;

const PageHeaderTitle = styled.div`
  overflow: hidden;
  width: 100%;
`;

const PageHeaderBreadcrumb = styled.div`
  overflow: hidden;
`;

type Props = {
  headerMode?: 'ios' | 'loggedIn';
  isMarket: boolean;
  extraHeaderButton?: ReactNode;
};

const Header = memo(function Header({
  isMarket,
  headerMode,
  extraHeaderButton,
}: Props) {
  const isDesktopAndAbove = useIsDesktopAndAbove();

  switch (headerMode) {
    case 'ios': {
      return (
        <IOSWrapper>
          <FullWidthContainer>
            <PageHeaderTitle id="page-header-title" />
            <PageHeaderBreadcrumb id="page-header-title-breadcrumb" />
          </FullWidthContainer>
        </IOSWrapper>
      );
    }
    case 'loggedIn': {
      return (
        <Container>
          <WebWrapper>
            <Left>
              <PageHeader>
                <PageHeaderTitle id="page-header-title" />
                <PageHeaderBreadcrumb id="page-header-title-breadcrumb" />
                <PageHeaderRightButton id="page-header-right-button" />
              </PageHeader>
            </Left>
            <Buttons>
              <Horizontal gap={isDesktopAndAbove ? 2 : 0.5}>
                <SubNav>
                  {extraHeaderButton}
                  {isDesktopAndAbove && (
                    <CoinsAndBalances compact={!isDesktopAndAbove} />
                  )}
                  {isMarket && !isDesktopAndAbove && (
                    <NotIOsAppFeature>
                      <Balances />
                    </NotIOsAppFeature>
                  )}
                  <ResponsiveSearchBar />
                  <Suspense fallback={null}>
                    <NavigationCart />
                  </Suspense>
                  <Suspense fallback={null}>
                    <Notifications />
                  </Suspense>
                </SubNav>
                <MasterSwitch />
              </Horizontal>
            </Buttons>
          </WebWrapper>
        </Container>
      );
    }
    default: {
      return <LandingAppHeader />;
    }
  }
});

export const AppHeader = ({
  extraHeaderButton,
}: {
  extraHeaderButton?: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const isLoggedIn = useIsLoggedIn();
  const { matchAnySportPath } = useSportContext();
  const { isIosApp } = useIsMobileApp();
  let headerMode: Props['headerMode'] = isIosApp ? 'ios' : undefined;
  headerMode ||= isLoggedIn ? 'loggedIn' : undefined;
  const isMarket = !!matchAnySportPath(catchAll(ANY_SPORT_MARKET), pathname);

  return (
    <Header
      headerMode={headerMode}
      isMarket={isMarket}
      extraHeaderButton={extraHeaderButton}
    />
  );
};
